import type { CheckInFormData, FormErrors } from '../types/checkin';
import { ISO_NATIONALITIES } from './referenceData';

/**
 * Validation de la fiche de police — Messages en français (Exigence 6.5, 6.6)
 * Mise à jour pour inclure les champs obligatoires pour la synchronisation CNS/ONT
 * Requirements: 2.1, 2.2, 2.3, 2.17
 */

// Regex pour validation email (format international)
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Regex pour validation téléphone (format international)
// Accepte: +242066000000, +33 6 12 34 56 78, (242) 066-000-000, etc.
const PHONE_REGEX = /^\+?[\d\s\-().]{6,20}$/;

// Champs obligatoires de la fiche de police (Exigence 5.1, 6.4)
// email, phoneNumber et profession sont FACULTATIFS (validés si fournis)
// documentPhotoUrl est OBLIGATOIRE car le scan OCR du passeport est requis
const REQUIRED_POLICE_FIELDS: (keyof CheckInFormData)[] = [
  'fullName',
  'gender',
  'dateOfBirth',
  'nationality',
  'documentType',
  'documentNumber',
  'documentExpiryDate',
  'roomType',
  'roomNumber',
  'checkInDate',
  'stayPurpose',
  'paymentType',
  'amount',
  // Scan du document obligatoire (lecture OCR du passeport)
  'documentPhotoUrl',
];

// Regex pour le format de numéro de document d'identité
const DOCUMENT_NUMBER_PATTERNS: Record<string, { regex: RegExp; hint: string }> = {
  'Passport': {
    regex: /^[A-Z0-9]{5,20}$/i,
    hint: 'Le numéro de passeport doit contenir entre 5 et 20 caractères alphanumériques',
  },
  'National ID Card (CNI)': {
    regex: /^[A-Z0-9]{5,30}$/i,
    hint: 'Le numéro de CNI doit contenir entre 5 et 30 caractères alphanumériques',
  },
  'Driving License': {
    regex: /^[A-Z0-9\-]{4,25}$/i,
    hint: 'Le numéro de permis doit contenir entre 4 et 25 caractères',
  },
  'Visa': {
    regex: /^[A-Z0-9]{5,25}$/i,
    hint: 'Le numéro de visa doit contenir entre 5 et 25 caractères alphanumériques',
  },
};

/**
 * Valider un champ individuel — messages en français
 */
export function validatePoliceField(
  name: keyof CheckInFormData,
  value: any,
  formData: CheckInFormData
): string {
  const stringValue = value != null && typeof value === 'object' ? '' : String(value || '');

  // Validation des champs obligatoires
  if (REQUIRED_POLICE_FIELDS.includes(name) && (!stringValue || stringValue.trim() === '')) {
    return 'Ce champ est obligatoire';
  }

  switch (name) {
    case 'fullName':
      if (stringValue && stringValue.trim().length > 0 && stringValue.trim().length < 2) {
        return 'Le nom complet doit contenir au moins 2 caractères';
      }
      break;

    case 'email':
      // Email est optionnel mais validé si fourni
      if (stringValue && stringValue.trim() !== '') {
        if (!EMAIL_REGEX.test(stringValue.trim())) {
          return 'Format d\'email invalide (ex: nom@domaine.com)';
        }
      }
      break;

    case 'phoneNumber':
      // Téléphone est optionnel mais validé si fourni
      if (stringValue && stringValue.trim() !== '') {
        if (!PHONE_REGEX.test(stringValue.trim())) {
          return 'Format de numéro de téléphone invalide (ex: +242066000000)';
        }
      }
      break;

    case 'documentPhotoUrl':
      // Scan du document obligatoire (lecture OCR du passeport)
      if (!stringValue || stringValue.trim() === '') {
        return 'Le scan du document d\'identité est obligatoire';
      }
      break;

    case 'facePhotoBase64':
      // Photo du visage est optionnelle
      break;

    case 'signatureBase64':
      // Signature est optionnelle
      break;

    case 'nationality':
      if (stringValue && stringValue.trim()) {
        const normalized = stringValue.trim();
        const isValid = ISO_NATIONALITIES.some(
          (n) => n.toLowerCase() === normalized.toLowerCase()
        );
        if (!isValid) {
          return 'Veuillez sélectionner une nationalité dans la liste';
        }
      }
      break;

    case 'dateOfBirth':
      if (stringValue && stringValue.trim()) {
        const dob = new Date(stringValue);
        if (isNaN(dob.getTime())) {
          return 'Format de date invalide';
        }
        const today = new Date();
        if (dob > today) {
          return 'La date de naissance ne peut pas être dans le futur';
        }
        const age = today.getFullYear() - dob.getFullYear();
        if (age > 150) {
          return 'Date de naissance invalide';
        }
      }
      break;

    case 'documentNumber':
      if (stringValue && stringValue.trim() && formData.documentType) {
        const pattern = DOCUMENT_NUMBER_PATTERNS[formData.documentType];
        if (pattern && !pattern.regex.test(stringValue.trim())) {
          return pattern.hint;
        }
      }
      break;

    case 'documentExpiryDate':
      if (stringValue && stringValue.trim()) {
        const expiry = new Date(stringValue);
        if (isNaN(expiry.getTime())) {
          return 'Format de date invalide';
        }
        const today = new Date();
        if (expiry < today) {
          return 'Attention : le document est expiré';
        }
      }
      break;

    case 'checkInDate':
      if (stringValue && stringValue.trim()) {
        const checkIn = new Date(stringValue);
        if (isNaN(checkIn.getTime())) {
          return 'Format de date invalide';
        }
      }
      break;

    case 'checkOutDate':
      if (stringValue && stringValue.trim() && formData.checkInDate) {
        const checkOut = new Date(stringValue);
        if (isNaN(checkOut.getTime())) {
          return 'Format de date invalide';
        }
        const checkIn = new Date(formData.checkInDate);
        if (checkOut <= checkIn) {
          return 'La date de départ doit être postérieure à la date d\'arrivée';
        }
      }
      break;

    case 'roomNumber':
      if (formData.roomType && (!stringValue || stringValue.trim() === '')) {
        return 'Le numéro de chambre est obligatoire';
      }
      break;

    case 'amount':
      if (stringValue && stringValue.trim()) {
        const amount = parseFloat(stringValue);
        if (isNaN(amount) || amount <= 0) {
          return 'Veuillez saisir un montant positif valide';
        }
      }
      break;

    case 'provenance':
      // Optional but validated if provided
      break;

    case 'stayPurpose':
      // Required — already handled by required fields check
      break;
  }

  return '';
}

/**
 * Valider le formulaire complet — messages en français
 */
export function validatePoliceForm(formData: CheckInFormData): FormErrors {
  const errors: FormErrors = {};

  Object.keys(formData).forEach((key) => {
    const fieldName = key as keyof CheckInFormData;
    const error = validatePoliceField(fieldName, formData[fieldName], formData);
    if (error) {
      errors[fieldName] = error;
    }
  });

  return errors;
}

/**
 * Calculer l'âge à partir de la date de naissance
 */
export function calculateAge(dateOfBirth: string): number {
  const dob = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

/**
 * Vérifier si le client est mineur
 */
export function isMinor(dateOfBirth: string): boolean {
  return calculateAge(dateOfBirth) < 18;
}

/**
 * Champs obligatoires pour la synchronisation CNS/ONT
 * Requirements: 2.1, 2.2, 2.3, 2.17
 */
export const SYNC_MANDATORY_FIELDS: (keyof CheckInFormData)[] = [
  'email',
  'phoneNumber',
  'documentPhotoUrl',
];

/**
 * Labels en français pour les champs obligatoires
 */
export const FIELD_LABELS: Record<string, string> = {
  fullName: 'Nom complet',
  gender: 'Genre',
  dateOfBirth: 'Date de naissance',
  nationality: 'Nationalité',
  documentType: 'Type de document',
  documentNumber: 'Numéro de document',
  documentExpiryDate: 'Date d\'expiration du document',
  roomType: 'Type de chambre',
  roomNumber: 'Numéro de chambre',
  checkInDate: 'Date d\'arrivée',
  stayPurpose: 'Motif du séjour',
  paymentType: 'Mode de paiement',
  amount: 'Montant',
  email: 'Adresse email',
  phoneNumber: 'Numéro de téléphone',
  documentPhotoUrl: 'Photo de la pièce d\'identité',
  facePhotoBase64: 'Photo du visage',
  signatureBase64: 'Signature',
};

/**
 * Valider les champs obligatoires pour la soumission/synchronisation
 * Retourne la liste des champs manquants avec leurs labels en français
 * Requirements: 2.1, 2.2, 2.3, 2.17
 */
export function validateForSubmission(formData: Partial<CheckInFormData>): {
  isValid: boolean;
  missingFields: string[];
  errors: FormErrors;
} {
  const errors: FormErrors = {};
  const missingFields: string[] = [];

  // Vérifier tous les champs obligatoires
  REQUIRED_POLICE_FIELDS.forEach((field) => {
    const value = formData[field];
    const stringValue = value != null && typeof value === 'object' ? '' : String(value || '');
    
    if (!stringValue || stringValue.trim() === '') {
      const label = FIELD_LABELS[field] || field;
      missingFields.push(label);
      errors[field] = `${label} est obligatoire`;
    }
  });

  // Validation spécifique pour email
  if (formData.email && formData.email.trim()) {
    if (!EMAIL_REGEX.test(formData.email.trim())) {
      errors.email = 'Format d\'email invalide (ex: nom@domaine.com)';
    }
  }

  // Validation spécifique pour téléphone
  if (formData.phoneNumber && formData.phoneNumber.trim()) {
    if (!PHONE_REGEX.test(formData.phoneNumber.trim())) {
      errors.phoneNumber = 'Format de numéro de téléphone invalide (ex: +242066000000)';
    }
  }

  return {
    isValid: missingFields.length === 0 && Object.keys(errors).length === 0,
    missingFields,
    errors,
  };
}

/**
 * Vérifier si les champs de synchronisation CNS/ONT sont remplis
 * Requirements: 2.1, 2.2, 2.3
 */
export function hasSyncMandatoryFields(formData: Partial<CheckInFormData>): boolean {
  return SYNC_MANDATORY_FIELDS.every((field) => {
    const value = formData[field];
    const stringValue = value != null && typeof value === 'object' ? '' : String(value || '');
    return stringValue && stringValue.trim() !== '';
  });
}

/**
 * Obtenir la liste des champs de synchronisation manquants
 * Requirements: 2.1, 2.2, 2.3
 */
export function getMissingSyncFields(formData: Partial<CheckInFormData>): string[] {
  const missing: string[] = [];
  
  SYNC_MANDATORY_FIELDS.forEach((field) => {
    const value = formData[field];
    const stringValue = value != null && typeof value === 'object' ? '' : String(value || '');
    
    if (!stringValue || stringValue.trim() === '') {
      missing.push(FIELD_LABELS[field] || field);
    }
  });
  
  return missing;
}

/**
 * Valider le format email
 */
export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

/**
 * Valider le format téléphone international
 */
export function isValidPhone(phone: string): boolean {
  return PHONE_REGEX.test(phone.trim());
}
